import { ref } from 'vue';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
/**
 * useFrameSpecData
 * @description
 * A composable function to manage frame specification data.
 * @param {Object} product - The product object containing details about the product
 * @param {string|null} [variantTitle=null] - The title of the product variant.
 * @param {string|null} [variantSpec=null] - The specification of the product variant.
 * @param {string|null} [variantId=null] - The ID of the product variant.
 */
function useFrameSpecData(product, variantTitle = null, variantSpec = null, variantId = null, allFullMouldings = null) {
  const frameSpec = ref(null);
  const frameSpecUrl = ref(null);
  const frameSpecPrice = ref(null);
  const isValid = ref(null);
  const canPrintArtwork = ref(null);
  const customerUploadActive = ref(null);
  const uploadWidgetEnabled = !!document.getElementById('uploadApp');
  const globalUploadWidgetEnabled = !!document.getElementById('globalUploadApp');
  const isUploadProcessing = ref(false);
  const buildFailed = ref(false);
  const showMaskRenderer = ref(false);

  const { type } = product || {};
  const eligibleForFrameSpec = type?.toLowerCase() === 'custom frame'
    || type?.toLowerCase() === 'art print frame'
    || type?.toLowerCase() === 'designers choice'
    || type?.toLowerCase() === 'curated design frame';

  const getUploadedArtwork = () => {
    // Check if uploaded artwork exists in localStorage
    const userUpload = localStorage.getItem('uploadedArtwork');

    // Update customerUploadActive to indicate if new uploads are allowed (based on widget presence)
    customerUploadActive.value = !!(uploadWidgetEnabled || globalUploadWidgetEnabled) && !!userUpload;

    if (userUpload && typeof userUpload === 'string') {
      try {
        const uploadedArtworkJSON = JSON.parse(userUpload);

        return uploadedArtworkJSON;
      } catch (error) {
        console.error({ message: 'Error parsing upload artwork JSON', error });
        return false;
      }
    }

    return false;
  };

  /**
   * Check FrameSpec Data
   * @description
   * Build a rendering only from an explicit theme upload (uploadedArtwork).
   * Framing-flow artwork (currentFrameSpec) is intentionally ignored here so it
   * never renders outside the framing flow; with no upload the default product
   * image is shown.
   * @returns {void}
   */
  function checkFrameSpecData() {
    if (!eligibleForFrameSpec) {
      buildFailed.value = true;
      return;
    }
    const userUpload = getUploadedArtwork();

    if (userUpload) {
      initUploadFrameSpec(userUpload, variantId);
      return;
    }

    // No explicit upload — nothing to render, fall back to default product image
    buildFailed.value = true;
  }

  async function initUploadFrameSpec(upload, vId) {
    // init loading state
    isUploadProcessing.value = true;
    const artTypeVals = {
      artTypeSlug: 'digital_upload',
      productCategory: 'digital_photo',
      productCategoryName: 'Digital Photo',
      conveyance: 'uploaded',
      medium: 'paper',
    };
    upload.artwork = {
      ...upload.artwork,
      ...artTypeVals,
    };

    try {
      const spec = await FrameSpec.build({
        joineryHost: SDG?.Data?.joineryHost || 'https://staging.framebridge.com',
        shopifyHost: SDG?.Data?.shopifyHost || 'https://staging.framebridge.dev',
        query: { product: `${product.id}`, variant: `${vId}` },
        artwork: upload.artwork,
        allFullMouldings,
      });
      const fsURL = await spec.urlParams({ stringify: true });
      const hasMask = spec?.hasMaskedRendering() || false;
      frameSpec.value = spec;
      isValid.value = true;
      frameSpecUrl.value = fsURL;
      canPrintArtwork.value = spec?.canPrintArtwork();
      showMaskRenderer.value = hasMask;

      if (spec) {
        setPrice(spec);
      }
    } catch (error) {
      console.error({ message: 'Error building FrameSpec', error });
      buildFailed.value = true;
    } finally {
      isUploadProcessing.value = false;
    }
  }

  /**
   * Update on Swatch Click
   * @description
   * updates the frameSpec values with the new moulding/variant
   * @param {string} - id
   */
  async function updateSpecOnSwatchClick(id) {
    const userUpload = getUploadedArtwork();
    try {
      if (userUpload) {
        initUploadFrameSpec(userUpload, id);
      }
    } catch (error) {
      console.error({ message: 'Error updating FrameSpec on swatch click', error });
    }
  }

  /**
   * Set Price
   * @description
   * Set the price in the FrameSpec
   * @param {Object} - clone
   * @returns {void}
   */
  function setPrice(clonedFrameSpec) {
    const { price } = clonedFrameSpec;
    // set the price with chosen size
    if (price && price?.total_price) {
      const money = price.total_price;

      frameSpecPrice.value = money;
    }
  }

  async function cloneFrameSpec(frameSpecItem) {
    try {
      const newFrameSpec = frameSpecItem.duplicate();
      if (!newFrameSpec) return null;

      await newFrameSpec?.updateVariantAndConstraints();

      if (newFrameSpec?.catalog?.shopifyVariantId) {
        return newFrameSpec;
      }
      console.error({ message: 'Error cloning FrameSpec', error: 'No shopify Variant Id' });

      return null;
    } catch (error) {
      console.error({ message: 'Error cloning FrameSpec', error });

      return null;
    }
  }

  async function getFrameSpecById(id, authorizationToken) {
    if (!id || !authorizationToken) return null;

    try {
      const frameSpecItem = await FrameSpec.build({
        joineryHost: SDG?.Data?.joineryHost || 'https://staging.framebridge.com',
        shopifyHost: SDG?.Data?.shopifyHost || 'https://staging.framebridge.dev',
        number: id,
        token: SDG.Data.joineryToken || 'f16fb23e225e68ba0cb41dbad9a95b3abbedc3f1fa461155',
        authorizationToken,
      });

      frameSpec.value = frameSpecItem;
      return frameSpecItem;
    } catch (error) {
      console.error({ message: 'Error getting frameSpec by id', error });

      return null;
    }
  }

  const clearFrameSpec = () => {
    frameSpec.value = null;
    frameSpecUrl.value = null;
    frameSpecPrice.value = null;
    isValid.value = null;
    canPrintArtwork.value = null;
    customerUploadActive.value = null;
    showMaskRenderer.value = false;
  };

  return {
    frameSpec,
    frameSpecPrice,
    frameSpecUrl,
    isValid,
    isUploadProcessing,
    buildFailed,
    canPrintArtwork,
    checkFrameSpecData,
    cloneFrameSpec,
    getFrameSpecById,
    updateSpecOnSwatchClick,
    customerUploadActive,
    clearFrameSpec,
    showMaskRenderer,
  };
}

export default useFrameSpecData;
